/**
 * Shared progress display for AI generation dialogs.
 * Shows a progress bar, counter, item grid, and trust messaging.
 */

import type { ReactNode } from 'react';
import styles from './AiDialog.module.css';

export interface ProgressItem {
  id: string;
  completed: boolean;
}

interface GenerationProgressProps {
  current: number;
  total: number;
  label: string;           // e.g. "shots", "lines", "videos"
  message?: string;        // e.g. "Generating shot 3/8..."
  items: ProgressItem[];
  /** Render a completed item (e.g., thumbnail, video, waveform icon) */
  renderComplete: (item: ProgressItem) => ReactNode;
  /** Render a pending item (e.g., shot number placeholder) */
  renderPending: (item: ProgressItem) => ReactNode;
  /** Use wider grid cells (for video content) */
  wide?: boolean;
}

export function GenerationProgress({
  current, total, label, message, items,
  renderComplete, renderPending, wide,
}: GenerationProgressProps) {
  const pct = total > 0 ? (current / total) * 100 : 0;

  return (
    <div className={styles.progressSection}>
      <div className={styles.progressCounter}>
        {current} of {total} {label}
      </div>
      <div className={styles.progressBar}>
        <div
          className={styles.progressFill}
          style={{ width: `${pct}%` }}
        />
      </div>
      {message && <div className={styles.progressText}>{message}</div>}
      {items.length > 0 && (
        <div className={`${styles.shotGrid} ${wide ? styles.shotGridWide : ''}`}>
          {items.map(item => (
            <div key={item.id} className={styles.shotGridItem}>
              {item.completed ? renderComplete(item) : renderPending(item)}
            </div>
          ))}
        </div>
      )}
      <div className={styles.trustMessageGenerating}>
        Credits deducted only on success
      </div>
    </div>
  );
}
